/// Chat input area with auto-expanding textarea, send/stop button,
/// model switcher dropdown, and settings gear icon.

import { useState, useCallback, useRef, useEffect } from "react";
import * as Select from "@radix-ui/react-select";
import { Send, Square, Settings, ChevronDown, Check } from "lucide-react";
import { useAIStore } from "@/stores/aiStore";
import { RECOMMENDED_MODELS, isModelFree } from "@/services/openrouter/models";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AIChatInputProps {
  onSend: (content: string) => void;
  onAbort: () => void;
  isStreaming: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AIChatInput({ onSend, onAbort, isStreaming }: AIChatInputProps) {
  const apiKey = useAIStore((s) => s.apiKey);
  const selectedModel = useAIStore((s) => s.selectedModel);
  const setSelectedModel = useAIStore((s) => s.setSelectedModel);
  const models = useAIStore((s) => s.models);
  const openSettings = useAIStore((s) => s.openSettings);

  const loadModels = useAIStore((s) => s.loadModels);

  const [input, setInput] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Load models on mount so the dropdown is always populated
  useEffect(() => {
    if (apiKey) loadModels();
  }, [apiKey, loadModels]);

  // Auto-expand textarea (max 4 lines)
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    const lineHeight = 20; // approximate line height in px
    const maxHeight = lineHeight * 4 + 16; // 4 lines + padding
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
  }, [input]);

  const handleSubmit = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;
    onSend(trimmed);
    setInput("");
  }, [input, isStreaming, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  // No API key: show setup prompt
  if (!apiKey) {
    return (
      <div className="border-t border-zinc-700 px-3 py-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-zinc-500">Set up AI to get started</span>
          <button
            type="button"
            onClick={openSettings}
            className="rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 transition-colors"
          >
            Configure AI
          </button>
        </div>
      </div>
    );
  }

  // Separate recommended from other models
  const recommendedSet = new Set(RECOMMENDED_MODELS);
  const recommendedModels = models.filter((m) => recommendedSet.has(m.id));
  const otherModels = models.filter((m) => !recommendedSet.has(m.id));

  // Derive a short display label for the selected model
  const selectedModelLabel = models.find((m) => m.id === selectedModel)?.name
    ?? selectedModel.split("/").pop()
    ?? "Select model";

  return (
    <div className="border-t border-zinc-700 px-3 py-2 space-y-2">
      {/* Model switcher + settings row */}
      <div className="flex items-center gap-2">
        <Select.Root value={selectedModel} onValueChange={setSelectedModel}>
          <Select.Trigger className="flex items-center gap-1 rounded border border-zinc-700 bg-zinc-800 px-2 py-1 text-[11px] text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors outline-none max-w-[200px]">
            <span className="truncate">{selectedModelLabel}</span>
            <Select.Icon>
              <ChevronDown size={12} />
            </Select.Icon>
          </Select.Trigger>

          <Select.Portal>
            <Select.Content
              className="z-50 rounded-lg border border-zinc-700 bg-zinc-900 shadow-xl overflow-hidden"
              position="popper"
              sideOffset={4}
              align="start"
            >
              <Select.ScrollUpButton className="flex items-center justify-center h-5 bg-zinc-900 text-zinc-400">
                <ChevronDown size={12} className="rotate-180" />
              </Select.ScrollUpButton>

              <Select.Viewport className="p-1 max-h-60">
                {/* Recommended models */}
                {recommendedModels.length > 0 && (
                  <Select.Group>
                    <Select.Label className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                      Recommended
                    </Select.Label>
                    {recommendedModels.map((model) => (
                      <ModelSelectItem
                        key={model.id}
                        modelId={model.id}
                        modelName={model.name}
                        isFree={isModelFree(model)}
                        isBold
                      />
                    ))}
                  </Select.Group>
                )}

                {/* Separator */}
                {recommendedModels.length > 0 && otherModels.length > 0 && (
                  <Select.Separator className="my-1 h-px bg-zinc-700" />
                )}

                {/* All other models */}
                {otherModels.length > 0 && (
                  <Select.Group>
                    <Select.Label className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                      All Models
                    </Select.Label>
                    {otherModels.map((model) => (
                      <ModelSelectItem
                        key={model.id}
                        modelId={model.id}
                        modelName={model.name}
                        isFree={isModelFree(model)}
                      />
                    ))}
                  </Select.Group>
                )}

                {/* No models loaded */}
                {models.length === 0 && (
                  <div className="px-2 py-3 text-center text-[10px] text-zinc-500">
                    No models loaded
                  </div>
                )}
              </Select.Viewport>

              <Select.ScrollDownButton className="flex items-center justify-center h-5 bg-zinc-900 text-zinc-400">
                <ChevronDown size={12} />
              </Select.ScrollDownButton>
            </Select.Content>
          </Select.Portal>
        </Select.Root>

        <button
          type="button"
          onClick={openSettings}
          className="rounded p-1 text-zinc-500 hover:text-zinc-300 transition-colors"
          aria-label="AI Settings"
        >
          <Settings size={14} />
        </button>
      </div>

      {/* Input row */}
      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isStreaming}
          placeholder="Ask InovaBPM AI..."
          rows={1}
          className="flex-1 resize-none rounded border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500/50 disabled:opacity-50"
        />

        {isStreaming ? (
          <button
            type="button"
            onClick={onAbort}
            className="flex items-center justify-center rounded bg-red-600/80 p-2 text-white hover:bg-red-600 transition-colors"
            aria-label="Stop generating"
          >
            <Square size={16} />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!input.trim()}
            className="flex items-center justify-center rounded bg-blue-600 p-2 text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Send message"
          >
            <Send size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Model select item
// ---------------------------------------------------------------------------

function ModelSelectItem({
  modelId,
  modelName,
  isFree,
  isBold,
}: {
  modelId: string;
  modelName: string;
  isFree: boolean;
  isBold?: boolean;
}) {
  return (
    <Select.Item
      value={modelId}
      className="relative flex items-center rounded px-2 py-1.5 text-xs text-zinc-300 outline-none cursor-pointer select-none data-[highlighted]:bg-zinc-800 data-[highlighted]:text-zinc-100"
    >
      <Select.ItemIndicator className="absolute left-0 flex items-center pl-1">
        <Check size={10} className="text-blue-400" />
      </Select.ItemIndicator>
      <span className={`pl-4 truncate ${isBold ? "font-semibold" : ""}`}>{modelName}</span>
      {isFree && (
        <span className="ml-auto pl-2 shrink-0 rounded px-1 py-0.5 text-[9px] font-medium bg-green-500/20 text-green-400">
          FREE
        </span>
      )}
    </Select.Item>
  );
}
